// src/pages/BookNow/BookNow.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  IconButton,
  Chip,
  Paper,
  Typography,
  TablePagination,
  CircularProgress,
  Modal,
  Alert,
  Snackbar,
} from "@mui/material";

import { Download, Visibility, Close } from "@mui/icons-material";
import {
  getEvoBookings,
  getCustomersBookingNotAttempted,
  getCustomerFailedBooking,
} from "../auth/service";
import moment from "moment";
import "./EvoBookings.css";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="booknow-wrapper">
          <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
            <Typography variant="h5" color="error" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              We're sorry, but an error occurred while loading this page.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Paper>
        </div>
      );
    }
    return this.props.children;
  }
}

const STATUS_CONFIG = {
  BOOKED: {
    label: "Booked",
    color: "#1976d2",
  },
  NOT_ATTEMPTED: {
    label: "Not Attempted",
    color: "#ff9800",
  },
  FAILED_BOOKING: {
    label: "Failed Booking",
    color: "#f44336",
  },
  CONFIRMED: {
    label: "Confirmed",
    color: "#2e7d32",
  },
  CANCELLED: {
    label: "Cancelled",
    color: "#d32f2f",
  },
  COMPLETED: {
    label: "Completed",
    color: "#4caf50",
  },
};

const EvoBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [componentError, setComponentError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filter states - default to BOOKED
  const [filters, setFilters] = useState({
    status: "BOOKED",
    search: "",
  });

  // Booking details modal states
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);

  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Helper function to check if current view is customer data (not EVO bookings)
  const isCustomerDataView = () => {
    const status = filters.status;
    return status === "NOT_ATTEMPTED" || status === "FAILED_BOOKING";
  };

  // Helper function to get payment method
  const getPaymentMethod = (booking) => {
    try {
      return booking?.transactionDetails?.paymentMethod || "N/A";
    } catch {
      return "N/A";
    }
  };

  // Helper function to get UPI VPA
  const getUpiVpa = (booking) => {
    try {
      return booking?.transactionDetails?.upiVpa || "N/A";
    } catch {
      return "N/A";
    }
  };

  // Helper function to get RRN
  const getRrn = (booking) => {
    try {
      return booking?.transactionDetails?.rrn || "N/A";
    } catch {
      return "N/A";
    }
  };

  // Helper function to get transaction ID directly from booking
  const getTransactionId = (booking) => {
    try {
      return booking?.transactionId || "N/A";
    } catch {
      return "N/A";
    }
  };

  // Helper function to extract meaningful error message from API response
  const getErrorMessage = (error) => {
    if (!error) return "Something went wrong";

    if (typeof error === "string") return error;

    if (error.message && typeof error.message === "string") {
      if (error.message === "{}" || error.message === "[object Object]") {
        return "Something went wrong";
      }
      return error.message;
    }

    if (error.response?.data?.message) {
      const msg = error.response.data.message;
      if (typeof msg === "object") {
        if (msg.message) return msg.message;
        if (msg.code) {
          const codeMessages = {
            AMOUNT_LIMIT_EXCEEDED:
              "Requested refund amount is greater than eligible amount",
          };
          return codeMessages[msg.code] || `Booking failed: ${msg.code}`;
        }
      }
      if (msg === "{}" || msg === "[object Object]") {
        return "Something went wrong";
      }
      return msg;
    }

    if (error.error && typeof error.error === "object") {
      if (error.error.message) {
        return error.error.message;
      }
    }

    return "Something went wrong";
  };

  // Safe string conversion helper
  const safeString = (value, defaultValue = "N/A") => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === "object") {
      return defaultValue;
    }
    return String(value);
  };

  // Safe number conversion helper
  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  // Safe object access helper
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      const keys = path.split(".");
      let result = obj;
      for (const key of keys) {
        if (result === null || result === undefined) return defaultValue;
        result = result[key];
      }
      return result !== undefined && result !== null ? result : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Function to get the appropriate API function based on status filter
  const getApiFunction = (status) => {
    switch (status) {
      case "NOT_ATTEMPTED":
        return getCustomersBookingNotAttempted;
      case "FAILED_BOOKING":
        return getCustomerFailedBooking;
      case "BOOKED":
      default:
        return getEvoBookings;
    }
  };

  // Fetch bookings with error handling
  const fetchBookings = async () => {
    setLoading(true);
    setComponentError(null);
    try {
      const apiFunction = getApiFunction(filters.status);

      console.log("Fetching bookings with params:", {
        page: page + 1,
        pageSize: rowsPerPage,
        search: filters.search,
        status: filters.status,
        apiFunction: apiFunction.name,
      });

      const res = await apiFunction({
        page: page + 1,
        pageSize: rowsPerPage,
        search: filters.search || null,
        status: filters.status || null,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      console.log("API Response:", res);

      // Handle different response structures
      if (isCustomerDataView()) {
        // For customer data views (NOT_ATTEMPTED, FAILED_BOOKING)
        if (res && res.user && Array.isArray(res.user.data)) {
          const customerData = res.user.data.map((customer) => {
            // For NOT_ATTEMPTED view - without transaction count
            if (filters.status === "NOT_ATTEMPTED") {
              return {
                _id: customer._id,
                name: customer.name,
                phone: customer.phone,
                fullUrl: customer.fullUrl,
                zohoRequestId: customer.zohoRequestId,
                createdAt: customer.createdAt,
                updatedAt: customer.updatedAt,
                countryCode: customer.countryCode,
                createdDate: customer.createdDate,
                modifiedDate: customer.modifiedDate,
                status: filters.status,
              };
            }
            // For FAILED_BOOKING view - with totalTransactions
            else {
              return {
                _id: customer._id,
                transactionId: customer.transactionId,
                customerId: customer.customerId,
                name: customer.name,
                phone: customer.phone,
                fullUrl: customer.fullUrl,
                zohoRequestId: customer.zohoRequestId,
                totalTransactions: customer.totalTransactions || 0,
                createdAt: customer.createdAt,
                updatedAt: customer.updatedAt,
                status: filters.status,
              };
            }
          });

          setBookings(customerData);
          setTotalCount(safeNumber(res.user.totalCount, customerData.length));
          setTotalAmount(0); // These views don't have total amount
          setTotalPages(safeNumber(res.user.totalPages, 1));
        } else {
          console.warn("Invalid response structure for customer data:", res);
          setBookings([]);
          setTotalCount(0);
          setTotalAmount(0);
          setTotalPages(1);

          if (!res) {
            showNotification("No response from server", "error");
          } else if (!res.user) {
            showNotification("Invalid response format", "error");
          } else {
            showNotification("No data found", "info");
          }
        }
      } else {
        // For EVO bookings view
        if (res && res.user && Array.isArray(res.user.data)) {
          const validBookings = res.user.data.filter(
            (booking) =>
              booking && typeof booking === "object" && !Array.isArray(booking),
          );
          setBookings(validBookings);
          setTotalCount(safeNumber(res.user.totalCount, validBookings.length));
          setTotalAmount(safeNumber(res.user.totalAmount, 0));
          setTotalPages(safeNumber(res.user.totalPages, 1));
        } else {
          console.warn("Invalid response structure:", res);
          setBookings([]);
          setTotalCount(0);
          setTotalAmount(0);
          setTotalPages(1);

          if (!res) {
            showNotification("No response from server", "error");
          } else if (!res.user) {
            showNotification("Invalid response format", "error");
          } else {
            showNotification("No bookings found", "info");
          }
        }
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookings([]);
      setTotalCount(0);
      setTotalPages(1);
      setComponentError(error);

      const errorMessage = getErrorMessage(error);
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage, filters.status, filters.search]);

  // Handle filter changes
  const handleStatusFilter = (event) => {
    try {
      setFilters({ ...filters, status: event.target.value });
      setPage(0);
    } catch (error) {
      console.error("Error in status filter:", error);
      showNotification("Error applying filter", "error");
    }
  };

  const handleSearchChange = (event) => {
    try {
      setFilters({ ...filters, search: event.target.value });
      setPage(0);
    } catch (error) {
      console.error("Error in search:", error);
      showNotification("Error applying search", "error");
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    try {
      setPage(newPage);
    } catch (error) {
      console.error("Error changing page:", error);
      showNotification("Error changing page", "error");
    }
  };

  const handleChangeRowsPerPage = (event) => {
    try {
      const newRowsPerPage = parseInt(event.target.value, 10);
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    } catch (error) {
      console.error("Error changing rows per page:", error);
      showNotification("Error changing rows per page", "error");
    }
  };

  // Handle view booking details with validation
  const handleViewBooking = (booking) => {
    try {
      if (!booking || typeof booking !== "object") {
        showNotification("Invalid data", "error");
        return;
      }
      setSelectedBooking(booking);
      setShowBookingDetails(true);
    } catch (error) {
      console.error("Error viewing details:", error);
      showNotification("Error viewing details", "error");
    }
  };

  // Show notification with proper error message
  const showNotification = (message, severity = "success") => {
    try {
      let displayMessage = "Something went wrong";

      if (typeof message === "string") {
        if (message && message !== "{}" && message !== "[object Object]") {
          displayMessage = message;
        }
      } else if (message && typeof message === "object") {
        const extractedMessage = getErrorMessage(message);
        if (
          extractedMessage &&
          extractedMessage !== "{}" &&
          extractedMessage !== "[object Object]"
        ) {
          displayMessage = extractedMessage;
        }
      }

      setSnackbar({
        open: true,
        message: displayMessage,
        severity: severity,
      });
    } catch (error) {
      console.error("Error showing notification:", error);
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  // Format currency safely
  const formatCurrency = (amount) => {
    try {
      const num = safeNumber(amount);
      return `₹${num.toLocaleString("en-IN")}`;
    } catch {
      return "₹0";
    }
  };

  // Format date safely
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return moment(dateString).format("DD-MMM-YYYY HH:mm");
    } catch {
      return "Invalid Date";
    }
  };

  // Handle export data
  const handleExportData = async () => {
    setExportLoading(true);
    try {
      const apiFunction = getApiFunction(filters.status);

      const res = await apiFunction({
        page: 1,
        pageSize: 10000,
        search: filters.search || null,
        status: filters.status || null,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      if (isCustomerDataView()) {
        // Export customer data
        if (res?.user?.data && Array.isArray(res.user.data)) {
          let exportData;

          if (filters.status === "NOT_ATTEMPTED") {
            exportData = res.user.data.map((customer) => ({
              "Customer ID": customer._id,
              "Customer Name": customer.name,
              Phone: customer.phone,
              "Country Code": customer.countryCode || "N/A",
              "Full URL": customer.fullUrl || "N/A",
              "Zoho Request ID": customer.zohoRequestId || "N/A",
              "Created Date": formatDate(customer.createdAt),
              "Created Date Only": customer.createdDate
                ? formatDate(customer.createdDate)
                : "N/A",
              "Modified Date": customer.modifiedDate
                ? formatDate(customer.modifiedDate)
                : "N/A",
            }));
          } else {
            // FAILED_BOOKING export with totalTransactions
            exportData = res.user.data.map((customer) => ({
              "Customer Name": customer.name,
              Phone: customer.phone,
              "Customer ID": customer._id,
              "Transaction ID": customer.transactionId || "N/A",
              "Customer ID Ref": customer.customerId || "N/A",
              "Total Transactions": customer.totalTransactions || 0,
              "Full URL": customer.fullUrl || "N/A",
              "Zoho Request ID": customer.zohoRequestId || "N/A",
              "Created Date": formatDate(customer.createdAt),
            }));
          }

          const XLSX = await import("xlsx");
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Customer_Data");

          const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
          });

          const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
          });

          const { default: saveAs } = await import("file-saver");
          saveAs(
            blob,
            `${filters.status}_${moment().format("DD-MMM-YYYY_HHmm")}.xlsx`,
          );

          showNotification("Export completed successfully", "success");
        } else {
          showNotification("No data to export", "warning");
        }
      } else {
        // Export EVO bookings data
        if (res?.user?.data && Array.isArray(res.user.data)) {
          const exportData = res.user.data.map((booking) => ({
            "Booking ID": booking._id,
            "Transaction ID": getTransactionId(booking),
            "Merchant Order Reference": safeGet(
              booking,
              "merchantOrderReference",
              "N/A",
            ),
            "PineLab Order ID": safeGet(booking, "pineLabOrderId", "N/A"),
            "Customer Name": safeGet(booking, "customerDetails.name", "N/A"),
            "Customer Phone": safeGet(booking, "customerDetails.phone", "N/A"),
            Amount: formatCurrency(booking.amount),
            Status: safeString(booking.status),
            "Booking Date": formatDate(booking.createdAt),
            "Payment Method": getPaymentMethod(booking),
            "Payment Status": safeGet(
              booking,
              "transactionDetails.paymentStatus",
              "N/A",
            ),
            "UPI VPA": getUpiVpa(booking),
            RRN: getRrn(booking),
            "Product Name": safeGet(
              booking,
              "productDetails.productName",
              "N/A",
            ),
            "Shipping City": safeGet(
              booking,
              "transactionDetails.shippingAddress.city",
              "N/A",
            ),
            "Shipping State": safeGet(
              booking,
              "transactionDetails.shippingAddress.state",
              "N/A",
            ),
          }));

          const XLSX = await import("xlsx");
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "EVO_Bookings");

          const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
          });

          const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
          });

          const { default: saveAs } = await import("file-saver");
          saveAs(
            blob,
            `EVO_Bookings_${moment().format("DD-MMM-YYYY_HHmm")}.xlsx`,
          );

          showNotification("Export completed successfully", "success");
        } else {
          showNotification("No data to export", "warning");
        }
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      const errorMessage = getErrorMessage(error);
      showNotification(errorMessage, "error");
    } finally {
      setExportLoading(false);
    }
  };

  // If component error, show error UI
  if (componentError) {
    return (
      <div className="booknow-wrapper">
        <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {getErrorMessage(componentError)}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => {
                setComponentError(null);
                fetchBookings();
              }}
            >
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Box>
        </Paper>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="booknow-wrapper">
        <div className="booknow-header-container">
          <span className="booknow-header">EVO Bookings</span>
          <div className="btn-container">
            {exportLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Button
                onClick={handleExportData}
                variant="contained"
                className="bordered-button"
                startIcon={<Download />}
                disabled={bookings.length === 0}
              >
                Export Data
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <Paper elevation={0} className="stat-card">
            <Typography variant="h6" color="textSecondary">
              Total Records
            </Typography>
            <Typography variant="h4">{totalCount}</Typography>
          </Paper>
          {!isCustomerDataView() && (
            <Paper elevation={0} className="stat-card">
              <Typography variant="h6" color="textSecondary">
                Total Amount
              </Typography>
              <Typography variant="h4">
                {formatCurrency(totalAmount)}
              </Typography>
            </Paper>
          )}
        </div>

        {/* Filters */}
        <div className="filters-container">
          <TextField
            label={
              isCustomerDataView()
                ? "Search by name or phone"
                : "Search by customer name or phone"
            }
            variant="outlined"
            value={filters.search}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 250 }}
          />
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel>View Type</InputLabel>
            <Select
              value={filters.status}
              label="View Type"
              onChange={handleStatusFilter}
            >
              <MenuItem value="BOOKED">Booked</MenuItem>
              <MenuItem value="NOT_ATTEMPTED">Not Attempted</MenuItem>
              <MenuItem value="FAILED_BOOKING">Failed Booking</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Data Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading data...
              </Typography>
            </div>
          ) : (
            <>
              <table className="bookings-table">
                <thead>
                  <tr>
                    {isCustomerDataView() ? (
                      <>
                        {filters.status === "NOT_ATTEMPTED" ? (
                          <>
                            <th>Customer Name</th>
                            <th>Phone Number</th>
                            <th>Full URL</th>
                            <th>Zoho Request ID</th>
                            <th>Status</th>
                            <th>Created Date</th>
                          </>
                        ) : (
                          <>
                            <th>Customer Name</th>
                            <th>Phone Number</th>
                            <th>Transaction ID</th>
                            <th>Customer ID</th>
                            <th>Total Transactions</th>
                            <th>Full URL</th>
                            <th>Zoho Request ID</th>
                            <th>Status</th>
                            <th>Created Date</th>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <th>Customer Name</th>
                        <th>Phone</th>
                        <th>Transaction ID</th>
                        <th>Order Reference</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Payment Method</th>
                        <th>Booking Date</th>
                        <th>Actions</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td
                        colSpan={
                          isCustomerDataView()
                            ? filters.status === "NOT_ATTEMPTED"
                              ? 6
                              : 9
                            : 9
                        }
                        className="no-data"
                      >
                        <Typography variant="body1">No data found</Typography>
                        <Typography variant="body2" color="textSecondary">
                          Try adjusting your search or filters
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    bookings.map((item) => {
                      try {
                        if (!item || typeof item !== "object") return null;

                        if (isCustomerDataView()) {
                          if (filters.status === "NOT_ATTEMPTED") {
                            // Render NOT_ATTEMPTED data row (without transaction count)
                            return (
                              <tr key={item._id}>
                                <td>{item.name || "N/A"}</td>
                                <td>{item.phone || "N/A"}</td>
                                <td>
                                  <a
                                    href={item.fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#1976d2",
                                      textDecoration: "none",
                                    }}
                                  >
                                    {item.fullUrl
                                      ? item.fullUrl.substring(0, 50)
                                      : "N/A"}
                                  </a>
                                </td>
                                <td>{item.zohoRequestId || "N/A"}</td>
                                <td>
                                  <Chip
                                    label={
                                      STATUS_CONFIG[filters.status]?.label ||
                                      filters.status
                                    }
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        STATUS_CONFIG[filters.status]?.color ||
                                        "#1976d2",
                                      color: "#fff",
                                    }}
                                  />
                                </td>
                                <td>{formatDate(item.createdAt)}</td>
                              </tr>
                            );
                          } else {
                            // Render FAILED_BOOKING data row (with totalTransactions)
                            return (
                              <tr key={item._id}>
                                <td>{item.name || "N/A"}</td>
                                <td>{item.phone || "N/A"}</td>
                                <td>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "12px",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {item.transactionId || "N/A"}
                                  </Typography>
                                </td>
                                <td>{item.customerId || "N/A"}</td>
                                <td>{item.totalTransactions || 0}</td>
                                <td>
                                  <a
                                    href={item.fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      color: "#1976d2",
                                      textDecoration: "none",
                                    }}
                                  >
                                    {item.fullUrl
                                      ? item.fullUrl.substring(0, 50)
                                      : "N/A"}
                                  </a>
                                </td>
                                <td>{item.zohoRequestId || "N/A"}</td>
                                <td>
                                  <Chip
                                    label={
                                      STATUS_CONFIG[filters.status]?.label ||
                                      filters.status
                                    }
                                    size="small"
                                    sx={{
                                      backgroundColor:
                                        STATUS_CONFIG[filters.status]?.color ||
                                        "#1976d2",
                                      color: "#fff",
                                    }}
                                  />
                                </td>
                                <td>{formatDate(item.createdAt)}</td>
                              </tr>
                            );
                          }
                        } else {
                          // Render EVO booking row
                          let statusConfig = STATUS_CONFIG[item.status];
                          if (!statusConfig) {
                            statusConfig = {
                              label: item.status || "Unknown",
                              color: "#757575",
                            };
                          }

                          return (
                            <tr key={item._id}>
                              <td>
                                {safeGet(item, "customerDetails.name", "N/A")}
                              </td>
                              <td>
                                {safeGet(item, "customerDetails.phone", "N/A")}
                              </td>
                              <td>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "12px",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {getTransactionId(item)}
                                </Typography>
                              </td>
                              <td>
                                <Typography
                                  variant="body2"
                                  sx={{ fontSize: "12px" }}
                                >
                                  {safeGet(
                                    item,
                                    "merchantOrderReference",
                                    "N/A",
                                  )}
                                </Typography>
                              </td>
                              <td>{formatCurrency(item.amount)}</td>
                              <td>
                                <Chip
                                  label={statusConfig.label}
                                  size="small"
                                  sx={{
                                    backgroundColor: statusConfig.color,
                                    color: "#fff",
                                  }}
                                />
                              </td>
                              <td>{getPaymentMethod(item)}</td>
                              <td>{formatDate(item.createdAt)}</td>
                              <td>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <IconButton
                                    onClick={() => handleViewBooking(item)}
                                    color="primary"
                                    size="small"
                                    title="View Details"
                                  >
                                    <Visibility />
                                  </IconButton>
                                </Box>
                              </td>
                            </tr>
                          );
                        }
                      } catch (error) {
                        console.error("Error rendering row:", error);
                        return null;
                      }
                    })
                  )}
                </tbody>
              </table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className="pagination"
                showFirstButton
                showLastButton
              />
            </>
          )}
        </div>

        {/* Details Modal */}
        <Modal
          open={showBookingDetails}
          onClose={() => setShowBookingDetails(false)}
          aria-labelledby="details-modal"
        >
          <Paper className="booking-details-modal">
            <div className="modal-header">
              <Typography variant="h5">
                {isCustomerDataView() ? "Customer Details" : "Booking Details"}
              </Typography>
              <IconButton onClick={() => setShowBookingDetails(false)}>
                <Close />
              </IconButton>
            </div>

            {selectedBooking && (
              <div className="modal-content">
                {isCustomerDataView() ? (
                  // Customer Details View
                  <>
                    <div className="details-section">
                      <Typography variant="h6" gutterBottom>
                        Customer Information
                      </Typography>
                      <div className="details-grid">
                        <div>
                          <strong>Customer ID:</strong> {selectedBooking._id}
                        </div>
                        <div>
                          <strong>Name:</strong> {selectedBooking.name || "N/A"}
                        </div>
                        <div>
                          <strong>Phone:</strong>{" "}
                          {selectedBooking.phone || "N/A"}
                        </div>

                        {filters.status === "NOT_ATTEMPTED" && (
                          <>
                            <div>
                              <strong>Country Code:</strong>{" "}
                              {selectedBooking.countryCode || "N/A"}
                            </div>
                            <div>
                              <strong>Created Date Only:</strong>{" "}
                              {selectedBooking.createdDate
                                ? formatDate(selectedBooking.createdDate)
                                : "N/A"}
                            </div>
                            <div>
                              <strong>Modified Date:</strong>{" "}
                              {selectedBooking.modifiedDate
                                ? formatDate(selectedBooking.modifiedDate)
                                : "N/A"}
                            </div>
                          </>
                        )}

                        {filters.status === "FAILED_BOOKING" && (
                          <>
                            <div>
                              <strong>Transaction ID:</strong>{" "}
                              {selectedBooking.transactionId || "N/A"}
                            </div>
                            <div>
                              <strong>Customer ID Ref:</strong>{" "}
                              {selectedBooking.customerId || "N/A"}
                            </div>
                            <div>
                              <strong>Total Transactions:</strong>{" "}
                              {selectedBooking.totalTransactions || 0}
                            </div>
                          </>
                        )}

                        <div>
                          <strong>Full URL:</strong>
                          <a
                            href={selectedBooking.fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#1976d2",
                              textDecoration: "none",
                              marginLeft: "8px",
                            }}
                          >
                            {selectedBooking.fullUrl || "N/A"}
                          </a>
                        </div>
                        <div>
                          <strong>Zoho Request ID:</strong>{" "}
                          {selectedBooking.zohoRequestId || "N/A"}
                        </div>
                        <div>
                          <strong>Created Date:</strong>{" "}
                          {formatDate(selectedBooking.createdAt)}
                        </div>
                        <div>
                          <strong>Last Updated:</strong>{" "}
                          {formatDate(selectedBooking.updatedAt)}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  // Booking Details View
                  <>
                    {/* Customer Information */}
                    <div className="details-section">
                      <Typography variant="h6" gutterBottom>
                        Customer Information
                      </Typography>
                      <div className="details-grid">
                        <div>
                          <strong>Name:</strong>{" "}
                          {safeGet(
                            selectedBooking,
                            "customerDetails.name",
                            "N/A",
                          )}
                        </div>
                        <div>
                          <strong>Phone:</strong>{" "}
                          {safeGet(
                            selectedBooking,
                            "customerDetails.phone",
                            "N/A",
                          )}
                        </div>
                        <div>
                          <strong>Country Code:</strong>{" "}
                          {safeGet(
                            selectedBooking,
                            "customerDetails.countryCode",
                            "N/A",
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Booking Information */}
                    <div className="details-section">
                      <Typography variant="h6" gutterBottom>
                        Booking Information
                      </Typography>
                      <div className="details-grid">
                        <div>
                          <strong>Booking ID:</strong> {selectedBooking._id}
                        </div>
                        <div>
                          <strong>Transaction ID:</strong>{" "}
                          {getTransactionId(selectedBooking)}
                        </div>
                        <div>
                          <strong>Merchant Order Reference:</strong>{" "}
                          {safeGet(
                            selectedBooking,
                            "merchantOrderReference",
                            "N/A",
                          )}
                        </div>
                        <div>
                          <strong>PineLab Order ID:</strong>{" "}
                          {safeGet(selectedBooking, "pineLabOrderId", "N/A")}
                        </div>
                        <div>
                          <strong>Amount:</strong>{" "}
                          {formatCurrency(selectedBooking.amount)}
                        </div>
                        <div>
                          <strong>Currency:</strong>{" "}
                          {safeGet(selectedBooking, "currency", "INR")}
                        </div>
                        <div>
                          <strong>Type:</strong>{" "}
                          {safeGet(selectedBooking, "type", "N/A")}
                        </div>
                        <div>
                          <strong>Status:</strong>
                          <Chip
                            label={
                              STATUS_CONFIG[selectedBooking.status]?.label ||
                              selectedBooking.status
                            }
                            size="small"
                            sx={{
                              backgroundColor:
                                STATUS_CONFIG[selectedBooking.status]?.color ||
                                "#1976d2",
                              color: "#fff",
                              ml: 1,
                            }}
                          />
                        </div>
                        <div>
                          <strong>Booking Date:</strong>{" "}
                          {formatDate(selectedBooking.createdAt)}
                        </div>
                        <div>
                          <strong>Last Updated:</strong>{" "}
                          {formatDate(selectedBooking.updatedAt)}
                        </div>
                      </div>
                    </div>

                    {/* Product Details */}
                    {selectedBooking.productDetails && (
                      <div className="details-section">
                        <Typography variant="h6" gutterBottom>
                          Product Details
                        </Typography>
                        <div className="details-grid">
                          <div>
                            <strong>Product Name:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "productDetails.productName",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>Description:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "productDetails.description",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>Total Price:</strong>{" "}
                            {formatCurrency(
                              safeGet(
                                selectedBooking,
                                "productDetails.totalPrice",
                                0,
                              ),
                            )}
                          </div>
                          <div>
                            <strong>Final Price:</strong>{" "}
                            {formatCurrency(
                              safeGet(
                                selectedBooking,
                                "productDetails.finalPrice",
                                0,
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Transaction Details */}
                    {selectedBooking.transactionDetails && (
                      <div className="details-section">
                        <Typography variant="h6" gutterBottom>
                          Transaction Details
                        </Typography>
                        <div className="details-grid">
                          <div>
                            <strong>Payment Method:</strong>{" "}
                            {getPaymentMethod(selectedBooking)}
                          </div>
                          <div>
                            <strong>Payment Status:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.paymentStatus",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>Transaction Status:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.status",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>Transaction Date:</strong>{" "}
                            {formatDate(
                              safeGet(
                                selectedBooking,
                                "transactionDetails.transactionCreatedAt",
                              ),
                            )}
                          </div>
                          <div>
                            <strong>Payment Success At:</strong>{" "}
                            {formatDate(
                              safeGet(
                                selectedBooking,
                                "transactionDetails.paymentSuccessAt",
                              ),
                            )}
                          </div>
                          {getUpiVpa(selectedBooking) !== "N/A" && (
                            <div>
                              <strong>UPI VPA:</strong>{" "}
                              {getUpiVpa(selectedBooking)}
                            </div>
                          )}
                          {getRrn(selectedBooking) !== "N/A" && (
                            <div>
                              <strong>RRN:</strong> {getRrn(selectedBooking)}
                            </div>
                          )}
                          {safeGet(
                            selectedBooking,
                            "transactionDetails.arn",
                          ) && (
                            <div>
                              <strong>ARN:</strong>{" "}
                              {selectedBooking.transactionDetails.arn}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Shipping Address */}
                    {selectedBooking.transactionDetails?.shippingAddress && (
                      <div className="details-section">
                        <Typography variant="h6" gutterBottom>
                          Shipping Address
                        </Typography>
                        <div className="details-grid">
                          <div>
                            <strong>Name:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.shippingAddress.name",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>Address:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.shippingAddress.address",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>City:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.shippingAddress.city",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>State:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.shippingAddress.state",
                              "N/A",
                            )}
                          </div>
                          <div>
                            <strong>Pincode:</strong>{" "}
                            {safeGet(
                              selectedBooking,
                              "transactionDetails.shippingAddress.pincode",
                              "N/A",
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </Paper>
        </Modal>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </ErrorBoundary>
  );
};

export default EvoBookings;
